'use client';

import { forwardRef, HTMLAttributes, ReactNode } from 'react';
import styles from './Badge.module.css';

type BadgeVariant = 'dot' | 'count';
type BadgeColor = 'primary' | 'secondary' | 'danger' | 'success';

export interface BadgeProps extends HTMLAttributes<HTMLDivElement> {
  variant?: BadgeVariant;
  color?: BadgeColor;
  count?: number;
  maxCount?: number;
  pulse?: boolean;
  visible?: boolean;
  /** Render inline (static) instead of as an overlay on children. Always shows, even at 0. */
  standalone?: boolean;
  children?: ReactNode;
}

const Badge = forwardRef<HTMLDivElement, BadgeProps>(({
  variant = 'count',
  color = 'primary',
  count = 0,
  maxCount = 99,
  pulse = false,
  visible = true,
  standalone = false,
  children,
  className = '',
  ...props
}, ref) => {
  const displayCount = count > maxCount ? `${maxCount}+` : String(count);
  const showBadge = visible && (standalone || variant === 'dot' || count > 0);

  return (
    <div
      ref={ref}
      className={[styles.wrapper, className].filter(Boolean).join(' ')}
      {...props}
    >
      {children}
      {showBadge && (
        <span
          className={[
            styles.badge,
            styles[variant],
            styles[color],
            standalone ? styles.standalone : '',
            pulse ? styles.pulse : '',
          ].filter(Boolean).join(' ')}
          aria-label={variant === 'count' ? `${count} notifications` : 'notification'}
        >
          {variant === 'count' && displayCount}
        </span>
      )}
    </div>
  );
});

Badge.displayName = 'Badge';
export default Badge;
